<template>
	<label
		v-if="canaryMarker"
		class="marker"
		:class="{ expanded: isExpanded, 'not-latest': isPrCanary && !prCanaryIsLatestCommit }"
		@click.stop.prevent="toggleExpanded"
	>
		<input type="checkbox" tabindex="-1" />

		<!-- Header section (always visible) -->
		<div class="header">
			<span class="expand-icon" v-if="isPrCanary" :class="{ rotated: isExpanded }">></span>
			<span class="canary">
				{{ canaryMarker }}<span v-if="isPrCanary && !prCanaryIsLatestCommit">⚠️ </span>
			</span>
			<button class="exit-canary" v-if="isPrCanary" type="button" @click.stop="exitPrCanary">Exit</button>
		</div>

		<!-- Expanded content section -->
		<div v-if="isExpanded && isPrCanary" class="expanded-content">
			<div class="pr-info">
				<div class="pr-title">
					<a :href="prCanaryPrUrl" target="_blank" rel="noopener noreferrer" @click.stop>
						{{ prCanaryPrTitle }}
					</a>
					<span class="deployment-type">{{ prCanaryDeploymentType }}</span>
				</div>
				<div class="pr-creator">by {{ prCanaryPrAuthor }}</div>
				<div class="commit-info">
					<div class="commit-message">{{ prCanaryLastCommitMsg }}</div>
					<div class="commit-date">
						{{ formatCommitDate(prCanaryLastCommitDate) }} - {{ prCanaryLastCommitId }}
						<span v-if="!prCanaryIsLatestCommit" class="not-latest-warning">(NOT LATEST)</span>
					</div>
				</div>
			</div>
			<div class="cookie-duration">
				<span>Auto Exit after:</span>
				<select @change.stop="onDurationChange" @click.stop>
					<option value="12h">12 hours (default)</option>
					<option value="2d">2 days</option>
					<option value="7d">One week</option>
					<option value="30d">One month</option>
				</select>
			</div>
		</div>
	</label>
</template>

<script setup lang="ts">
import { useAppStore } from '@/stores/pinia/useAppStore';
import { computed, ref } from 'vue';

const { jwConfig } = useRuntimeConfig().public;

const appStore = useAppStore();

// Helper function to format date
function formatCommitDate(dateStr: string) {
	const date = new Date(dateStr);
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const diffMins = Math.floor(diffMs / (1000 * 60));
	const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
	const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

	if (diffMins < 1) return 'just now';
	if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
	if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
	if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

	// For older commits, show the actual date and time
	return date.toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	});
}

// Expanded state
const isExpanded = ref(false);

const canaryMarker = computed(() => {
	const envCanary = jwConfig.PR_CANARY_FLAG;
	if (envCanary) return envCanary;

	if (appStore.xServeTrack == null || appStore.xServeTrack === 'prod' || appStore.xServeTrack === 'canary-public')
		return null;

	return appStore.xServeTrack.replaceAll('-', ' ');
});

const isPrCanary = jwConfig.PR_CANARY_FLAG != null;

const prCanaryPrTitle = jwConfig.PR_CANARY_PR_TITLE;
const prCanaryPrAuthor = jwConfig.PR_CANARY_PR_AUTHOR;
const prCanaryPrUrl = jwConfig.PR_CANARY_PR_URL;
const prCanaryLastCommitMsg = jwConfig.PR_CANARY_LAST_COMMIT_MSG;
const prCanaryLastCommitDate = jwConfig.PR_CANARY_LAST_COMMIT_DATE;
const prCanaryLastCommitId = jwConfig.PR_CANARY_LAST_COMMIT_ID;
const prCanaryIsLatestCommit = jwConfig.PR_CANARY_IS_LATEST_COMMIT ?? true;
const prCanaryDeploymentType = jwConfig.PR_CANARY_DEPLOYMENT_TYPE ?? 'SSR';

function toggleExpanded() {
	isExpanded.value = !isExpanded.value;
}

function exitPrCanary() {
	const url = new URL(window.location.href);
	url.searchParams.set('exit-pr-canary', 'true');
	url.searchParams.delete('pr-canary');
	window.location.href = url.toString();
}

function onDurationChange(event: Event) {
	const duration = (event.target as HTMLSelectElement).value;
	const url = new URL(window.location.href);
	url.searchParams.set('pr-canary-extend', duration);
	window.location.href = url.toString();
}
</script>

<style lang="scss" scoped>
.marker {
	--color: rgba(251, 197, 0, 1);

	&.not-latest {
		--color: rgb(201, 114, 7);
	}

	position: fixed;
	bottom: 10px;
	right: 15px;
	z-index: 999999999;

	display: inline-flex;
	flex-direction: column;
	cursor: pointer;
	color: #060d17;
	background-color: var(--color);
	border-radius: 8px;
	pointer-events: all;
	line-height: 20px;
	transition: all 0.3s ease;
	min-width: fit-content;

	&.expanded {
		min-width: 280px;
		max-width: 400px;
	}

	.header {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		padding: 4px 12px;
	}

	.expand-icon {
		font-size: 16px;
		font-weight: bold;
		transition: transform 0.2s ease;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 16px;
		height: 16px;

		&.rotated {
			transform: rotate(90deg);
		}
	}

	.canary {
		margin-bottom: 1px;
		flex: 1;
	}

	.deployment-type {
		font-size: 0.75em;
		font-weight: 600;
		padding: 2px 6px;
		background-color: rgba(0, 0, 0, 0.15);
		border-radius: 4px;
		text-transform: uppercase;
		letter-spacing: 0.5px;
		flex-shrink: 0;
	}

	.expanded-content {
		border-top: 1px solid rgba(0, 0, 0, 0.1);
		padding: 8px 12px;
		background-color: rgba(0, 0, 0, 0.05);
		border-radius: 0 0 8px 8px;
	}

	.pr-info {
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	.pr-title {
		font-weight: 600;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;

		a {
			color: #060d17;
			text-decoration: none;
			flex: 1;

			&:hover {
				text-decoration: underline;
			}
		}
	}

	.pr-creator {
		font-size: 0.9em;
		opacity: 0.8;
	}

	.commit-info {
		margin-top: 8px;
		padding-top: 8px;
		border-top: 1px solid rgba(0, 0, 0, 0.1);
	}

	.commit-message {
		font-size: 0.9em;
		font-style: italic;
		margin-bottom: 4px;
		word-wrap: break-word;
	}

	.commit-date {
		font-size: 0.8em;
		opacity: 0.7;
		font-weight: 500;
	}

	.not-latest-warning {
		color: rgb(129, 5, 5) 2;
		font-weight: bold;
		margin-left: 4px;
		font-size: 1.3em;
	}

	&:has(input[type='checkbox']:checked) {
		display: none;
	}

	// hide the checkbox
	input[type='checkbox'] {
		position: absolute;
		width: 1px;
		height: 1px;
		padding: 0;
		margin: -1px;
		overflow: hidden;
		clip: rect(0, 0, 0, 0);
		white-space: nowrap;
		border-width: 0;
	}

	.cookie-duration {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-top: 8px;
		padding-top: 8px;
		border-top: 1px solid rgba(0, 0, 0, 0.1);
		font-size: 0.9em;

		select {
			background: rgba(0, 0, 0, 0.08);
			border: none;
			border-radius: 4px;
			padding: 2px 6px;
			color: #060d17;
			cursor: pointer;
			font-size: inherit;

			&:hover {
				background: rgba(0, 0, 0, 0.172);
			}
		}
	}

	.exit-canary {
		background: none;
		border: none;
		color: #060d17;
		font-weight: bold;
		padding: 2px 8px;
		margin-left: 8px;
		border-radius: 4px;
		cursor: pointer;
		transition: background 0.2s;
		background: rgba(0, 0, 0, 0.08);

		&:hover {
			background: rgba(0, 0, 0, 0.172);
		}
	}
}
</style>
